import React from "react"

import { formatCurrency } from "~/utils/helpers"
import { buildEmptyPaymentItem } from "~/utils/paymentModalHelpers"

import type PaymentItem from "~/types/PaymentItem"
import type SavedPaymentMethod from "~/types/SavedPaymentMethod"

interface Props {
  title: string
  items: PaymentItem[]
  setItems: React.Dispatch<React.SetStateAction<PaymentItem[]>>
  currency: string
  isSubmitting: boolean
  placeholderTitle: string
  isSavedCardFlow?: boolean
  activeSavedCard?: SavedPaymentMethod | null
  processingFeeCents?: number
  processingFeePayer?: "team_pays" | "fan_pays"
}

const PaymentItemsTable: React.FC<Props> = ({
  title,
  items,
  setItems,
  currency,
  isSubmitting,
  placeholderTitle,
  isSavedCardFlow = false,
  activeSavedCard = null,
  processingFeeCents = 0,
  processingFeePayer = "team_pays",
}) => {
  const visibleItems = items.filter((item) => !item._destroy)
  const totalCents = visibleItems.reduce(
    (total, item) => total + item.quantity * item.amountCents,
    0
  )
  const customerPaysFee = processingFeePayer === "fan_pays"
  const addPaymentItem = () => {
    setItems((prev) => [...prev, buildEmptyPaymentItem(currency)])
  }

  const removePaymentItem = (index: number) => {
    setItems((prev) => {
      const updatedItems = [...prev]

      if (updatedItems[index].id) {
        updatedItems[index] = { ...updatedItems[index], _destroy: true }
      } else {
        updatedItems.splice(index, 1)
      }

      return updatedItems
    })
  }

  const updatePaymentItem = (
    index: number,
    field: keyof PaymentItem,
    value: PaymentItem[keyof PaymentItem]
  ) => {
    setItems((prev) => {
      const updatedItems = [...prev]
      updatedItems[index] = { ...updatedItems[index], [field]: value }
      return updatedItems
    })
  }

  return (
    <div>
      <h6 className="text-muted mb-3">{title}</h6>

      <div className="table-responsive">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Subtotal</th>
              <th style={{ width: "50px" }}></th>
            </tr>
          </thead>

          <tbody>
            {items.map((item, actualIndex) => {
              if (item._destroy) return null

              return (
                <tr key={item.id || `payment-item-${actualIndex}`} className="align-middle">
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={item.description}
                      onChange={(e) =>
                        updatePaymentItem(actualIndex, "description", e.target.value)
                      }
                      placeholder={placeholderTitle}
                      required
                      disabled={isSubmitting}
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.quantity}
                      onChange={(e) =>
                        updatePaymentItem(
                          actualIndex,
                          "quantity",
                          parseInt(e.target.value, 10) || 1
                        )
                      }
                      min="1"
                      style={{ width: "80px" }}
                      required
                      disabled={isSubmitting}
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.amountCents / 100}
                      onChange={(e) =>
                        updatePaymentItem(
                          actualIndex,
                          "amountCents",
                          Math.round((parseFloat(e.target.value) || 0) * 100)
                        )
                      }
                      step="0.01"
                      min="0"
                      style={{ width: "100px" }}
                      placeholder="0.00"
                      required
                      disabled={isSubmitting}
                    />
                  </td>

                  <td>
                    {formatCurrency({
                      cents: item.quantity * item.amountCents,
                      currency_iso: currency,
                    })}
                  </td>

                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removePaymentItem(actualIndex)}
                      disabled={visibleItems.length === 1 || isSubmitting}
                      title="Remove item"
                    >
                      x
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>

          <tfoot>
            <tr>
              <td colSpan={3}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={addPaymentItem}
                  disabled={isSubmitting || (isSavedCardFlow && !activeSavedCard)}
                >
                  + Add Item
                </button>
              </td>
              <th>{formatCurrency({ cents: totalCents, currency_iso: currency })}</th>
              <td></td>
            </tr>
            {processingFeeCents > 0 && (
              <tr>
                <td colSpan={3} className="text-end">
                  {customerPaysFee
                    ? "Processing fee"
                    : "Processing fee (deducted from venue proceeds)"}
                </td>
                <th>{formatCurrency({ cents: processingFeeCents, currency_iso: currency })}</th>
                <td></td>
              </tr>
            )}
            <tr>
              <td colSpan={3} className="text-end">
                Total
              </td>
              <th>
                {formatCurrency({
                  cents: totalCents + (customerPaysFee ? processingFeeCents : 0),
                  currency_iso: currency,
                })}
              </th>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}

export default PaymentItemsTable
